import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Card,
  Divider,
  Grid,
  CardContent,
  CardHeader,
  Link,
  CircularProgress,
} from "@mui/material";
import { PageBar } from "../../CommonComponents";
import Typography from "@mui/material/Typography";
import InventoryButton from "../../CommonComponents/InventoryButton/InventoryButton";
import UploadFiles from "./UploadFiles";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { post } from "../../services";
import { isDevelopment } from "../../Hooks/constants";

const boxSX = {
  textDecoration: "none",
  "&:hover": {
    cursor: "pointer",
    textDecoration: "underline",
    color: "darkblue",
  },
};

const Index = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Dynamic Base URL based on Environment Variables
  const base = isDevelopment
    ? `${process.env.REACT_APP_SCRAP_SALE_DEV}`
    : `${process.env.REACT_APP_SCRAP_SALE_PROD}`;

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };
  const handlePdfFileSelect = (e) => {
    setSelectedPdfFile(e.target.files[0]);
  };

  const handleSampleFile = async (type) => {
    if (!type) {
      toast.error("Please send download type", { position: "top-right" });
      return;
    }
    try {
      setLoading(true);
      const response = await post(
        base,
        {
          rawPath: "/generate_scrap_data",
          type: type,
        },
        true,
        false
      );
      const fileUrl = response?.body?.data || response?.data?.body?.data;
      if (!fileUrl) {
        setLoading(false);
        toast.error(
          response?.body?.message ||
          response?.data?.body?.message ||
          "Sample file download error",
          { position: "top-right" }
        );
        return;
      } else {
        setLoading(false);
        toast.success("File downloaded successfully", {
          position: "top-right",
        });
        window.open(fileUrl, "_blank");
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      toast.error("Something went wrong", { position: "top-right" });
    }
  };

  const uploadFileWithPresignedUrl = async (filetoUpload, generatedUrl) => {
    try {
      const fileUploadData = new FormData();
      Object.keys(generatedUrl.fields).forEach((key) => {
        fileUploadData.append(key, generatedUrl.fields[key]);
      });
      console.log("File to Upload:", filetoUpload);
      fileUploadData.append("file", filetoUpload);

      const fileUploaded = await uploadFile(generatedUrl.url, fileUploadData);

      if (fileUploaded) {
        return true;
      } else {
        toast.error("File upload failed", { position: "top-right" });
        return false;
      }
    } catch (error) {
      toast.error("Error during file upload:", { position: "top-right" });
      return false;
    }
  };

  const uploadFile = async (url, formData) => {
    try {
      const response = await axios.post(url, formData);

      if (response.status === 204) {
        console.log("File upload successful with status 204");
        return true;
      }

      if (response.status >= 200 && response.status < 300) {
        console.log("File upload successful");
        return true;
      } else {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error during file upload:", error);
      throw error;
    }
  };

  const handleFileSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please select Order details file.", {
        position: "top-right",
      });
      return;
    }
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
    if (fileExtension !== "xlsx") {
      toast.error("Order details file must be of excel format.", {
        position: "top-right",
      });
      return;
    }
    if (selectedPdfFile) {
      const pdfFileExtension = selectedPdfFile.name
        .split(".")
        .pop()
        .toLowerCase();
      const specialCharsRegex = /[^A-Za-z0-9_ ().]/g;
      const pdfFileName = selectedPdfFile.name;

      if (
        pdfFileExtension !== "pdf" &&
        pdfFileExtension !== "eml" &&
        pdfFileExtension !== "zip"
      ) {
        toast.error(
          "Vendor document must be either in PDF, EML, or ZIP format",
          { position: "top-right" }
        );
        return;
      }

      if (specialCharsRegex.test(pdfFileName)) {
        toast.error(
          "Only numbers, alphabets, underscore, and space are allowed in the file name",
          { position: "top-right" }
        );
        return;
      }
    }
    const xlsxFileName = selectedFile.name;
    const specialCharsRegex = /[^A-Za-z0-9_ ().]/g;

    if (specialCharsRegex.test(xlsxFileName)) {
      toast.error(
        "Only numbers, alphabets, underscore, and space are allowed in the file name",
        { position: "top-right" }
      );
      return;
    }
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:.]/g, "")
      .slice(0, 15);
    const folderName = "scrapSaleUploads";
    const pdffolderName = "scrapSaleUploads/vendor_documents";
    const filenameWithTimestamp = `${folderName}/${selectedFile.name
      .split(".")
      .slice(0, -1)
      .join(".")}_${timestamp}.${selectedFile.name.split(".").pop()}`;
    const pdfFilenameWithTimestamp = selectedPdfFile
      ? `${pdffolderName}/${selectedPdfFile.name
        .split(".")
        .slice(0, -1)
        .join(".")}_${timestamp}.${selectedPdfFile.name.split(".").pop()}`
      : null;
    try {
      setLoading(true);
      const ordersFileURL = await post(
        base,
        {
          rawPath: "/gen_pre_url",
          filename: filenameWithTimestamp,
        },
        true,
        false
      );

      const ordersPresignedData =
        ordersFileURL?.data?.response ||
        ordersFileURL?.response ||
        ordersFileURL?.body?.response ||
        ordersFileURL?.body;

      await uploadFileWithPresignedUrl(
        selectedFile,
        ordersPresignedData
      );
      let pdfPresignedUrlResponse;
      let pdfPresignedUrl;
      if (selectedPdfFile) {
        pdfPresignedUrlResponse = await post(
          base,
          {
            rawPath: "/gen_pre_url",
            filename: pdfFilenameWithTimestamp,
          },
          true,
          false
        );

        pdfPresignedUrl =
          pdfPresignedUrlResponse?.data?.response ||
          pdfPresignedUrlResponse?.response ||
          pdfPresignedUrlResponse?.body?.response ||
          pdfPresignedUrlResponse?.body;
        if (!pdfPresignedUrl) {
          throw new Error("Presigned URL for PDF file is not valid");
          setLoading(false);
        }
        await uploadFileWithPresignedUrl(selectedPdfFile, pdfPresignedUrl);
      }
      await post(
        base,
        {
          rawPath: "/create_scrap_order",
          file_name:
            ordersPresignedData?.fields?.key ||
            ordersFileURL?.data?.response?.fields?.key ||
            "",
          vendor_doc:
            pdfPresignedUrl?.fields?.key ||
            pdfPresignedUrlResponse?.data?.response?.fields?.key ||
            "",
          module_name: "scrap_sale",
          source: "SCRAP",
        },
        true,
        false
      )
        .then((response) => {
          if (response?.status === "Success") {
            toast.success("File uploaded successfully", {
              position: "top-right",
            });
            setTimeout(() => {
              setLoading(false);
              window.location.reload();
            }, 1500);
          } else if (response?.status === "Error") {
            toast.error(response?.body?.message || "File uploading failed", {
              position: "top-right",
            });
            setLoading(false);
            setTimeout(() => {
              window.location.reload();
            });
          }
        })
        .catch((error) => {
          console.log("error", error);
          setLoading(false);
          setTimeout(() => {
            window.location.reload();
          });
        });
    } catch (err) {
      console.log(err);
      setLoading(false);
      toast.error("Something went wrong", { position: "top-right" });
      setTimeout(() => {
        window.location.reload();
      });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#EEF2F7",
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      <PageBar text={"Scrap Sale: Billing"} />
      <Card
        sx={{
          backgroundColor: "#EEF2F7",
          padding: "1.3rem",
        }}
      >
        <CardHeader
          p={2}
          sx={{
            backgroundColor: "#F8F8F9",
            fontWeight: "700",
            borderTopRightRadius: "5px",
            borderTopLeftRadius: "5px",
          }}
          title={"Order Upload "}
          titleTypographyProps={{
            sx: {
              fontSize: "16px",
              fontWeight: "600",
              lineHeight: "16px",
              color: "#0160F1",
            },
          }}
        />
        <Divider />
        <CardContent
          sx={{
            backgroundColor: "#FFFFFF",
            borderBottomRightRadius: "5px",
            borderBottomLeftRadius: "5px",
            fontSize: 15,
            // marginBottom: -5
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Grid container rowSpacing={1}>
              <Grid item xs={5} sx={{ backgroundColor: "white" }}>
                <Typography>
                  <b style={{ color: "red" }}>&nbsp;&nbsp;&nbsp;*&nbsp;</b>
                  Upload your order details
                </Typography>
                <Box
                  p={2}
                  sx={{
                    display: { lg: "flex", md: "flex" },
                    gap: "1rem",
                  }}
                >
                  <Box
                    p={1}
                    sx={{
                      border: "1px solid lightgrey",
                      borderRadius: "5px",
                      marginBottom: { sm: "1rem" },
                    }}
                  >
                    <input
                      type="file"
                      multiple={false}
                      onChange={handleFileSelect}
                      accept=".xls,.xlsx"
                    />
                  </Box>
                </Box>
              </Grid>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  marginRight: 5,
                  marginLeft: 5,
                  backgroundColor: "#c0c0c0",
                  width: 2,
                }}
              />

              <Grid item xs={6}>
                <Typography>
                  <b style={{ color: "red" }}></b>Upload Vendor Document
                  (PDF/EML/ZIP file only)
                </Typography>

                <Box
                  p={2}
                  sx={{
                    display: { lg: "flex", md: "flex" },
                    gap: "1rem",
                  }}
                >
                  <Box
                    p={1}
                    sx={{
                      border: "1px solid lightgrey",
                      borderRadius: "5px",
                      marginBottom: { sm: "1rem" },
                    }}
                  >
                    <input
                      type="file"
                      multiple={false}
                      onChange={handlePdfFileSelect}
                      accept=".pdf,.zip,.eml,"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1}>
              <Box
                p={1}
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  lineHeight: "16px",
                  marginLeft: "1rem",
                }}
              >
                <Link
                  component={"button"}
                  color={"#0160F1"}
                  sx={boxSX}
                  onClick={() => {
                    handleSampleFile("samplefile");
                  }}
                >
                  Download Sample File
                </Link>
              </Box>

              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: "1.2rem",
                  marginTop: 1,
                  backgroundColor: "#c0c0c0",
                  width: 2,
                }}
              />
              <Box
                p={1}
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  lineHeight: "16px",
                  marginLeft: "1rem",
                }}
              >
                <Link
                  component={"button"}
                  color={"#0160F1"}
                  sx={boxSX}
                  onClick={() => {
                    handleSampleFile("customer_master");
                  }}
                >
                  Download Customer Master
                </Link>
              </Box>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: "1.2rem",
                  marginTop: 1,
                  backgroundColor: "#c0c0c0",
                  width: 2,
                }}
              />
              <Box
                p={1}
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  lineHeight: "16px",
                  marginLeft: "1rem",
                }}
              >
                <Link
                  component={"button"}
                  color={"#0160F1"}
                  sx={boxSX}
                  onClick={() => {
                    handleSampleFile("receiving_details");
                  }}
                >
                  Download Receiving Details
                </Link>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <InventoryButton
                  btnText={"SAVE DATA"}
                  btnFunction={handleFileSubmit}
                  loading={loading}
                />
              </Box>
            </Grid>
          </Box>
        </CardContent>
        <UploadFiles />
      </Card>
    </Box>
  );
};

export default Index;
